namespace $ {
	export type $bog_vmap_app_wire_box = {
		readonly left: number
		readonly top: number
		readonly width: number
		readonly height: number
	}

	export type $bog_vmap_app_wire_port = {
		readonly name: string
		readonly next: boolean
		readonly own: boolean
		readonly kind: $bog_vmap_app_inspect_value_kind
		readonly label?: string
	}

	export type $bog_vmap_app_wire_side = 'in' | 'out'

	export type $bog_vmap_app_wire_dot = {
		readonly node: string
		readonly port: $bog_vmap_app_wire_port
		readonly side: $bog_vmap_app_wire_side
		readonly x: number
		readonly y: number
		readonly lit: boolean
		readonly linked: boolean
		readonly hint: string
	}

	export type $bog_vmap_app_wire_line = {
		readonly key: string
		readonly geometry: string
		readonly label: string
		readonly label_x: number
		readonly label_y: number
		readonly bidi: boolean
	}

	export const $bog_vmap_app_wire_sign = '⇄'

	export const $bog_vmap_app_wire_hint = 'Shift — двусторонний'

	export const $bog_vmap_app_wire_row = 14

	export const $bog_vmap_app_wire_gap = 12

	export const $bog_vmap_app_wire_radius = 5

	export const $bog_vmap_app_wire_hit = 8

	const wirable = new Set< $bog_vmap_app_inspect_value_kind >([
		'string', 'number', 'bool', 'null', 'locale', 'list', 'get', 'bind',
	])

	export const $bog_vmap_app_wire_machinery = new Set([
		'dom_name', 'sub', 'attr', 'style', 'event', 'field',
	])

	export function $bog_vmap_app_wire_plain( port: { readonly name: string } ) {
		return !$bog_vmap_app_wire_machinery.has( port.name )
	}

	export function $bog_vmap_app_wire_slot( port: {
		readonly kind: $bog_vmap_app_inspect_value_kind
		readonly next: boolean
	} ) {
		return port.kind === 'null' && !port.next
	}

	export const $bog_vmap_app_wire_slots = 'slots'

	const label_ok = /^[A-Za-z_$][\w$]*$/

	export function $bog_vmap_app_wire_labelled(
		ports: readonly $bog_vmap_app_wire_port[],
		labels: readonly string[],
	): readonly $bog_vmap_app_wire_port[] {

		if( !labels.length ) return ports

		let at = 0

		return ports.map( port => {

			if( !$bog_vmap_app_wire_slot( port ) ) return port

			const label = labels[ at ++ ] ?? ''

			return label_ok.test( label ) ? { ... port, label } : port
		} )
	}

	export function $bog_vmap_app_wire_ports(
		this: $,
		props: ReadonlyMap< string, $mol_tree2 >,
		owners: ReadonlyMap< string, string >,
		base: string,
	): readonly $bog_vmap_app_wire_port[] {
		const ports = [] as $bog_vmap_app_wire_port[]

		for( const [ name, prop ] of props ) {
			const meta = this.$mol_view_tree2_prop_parts( prop )
			if( meta.key ) continue

			const kind = this.$bog_vmap_app_inspect_value_kind_of( prop.kids[ 0 ] ?? null )
			if( !wirable.has( kind ) ) continue

			ports.push({ name, next: Boolean( meta.next ), own: owners.get( name ) === base, kind })

		}

		return ports
	}

	export function $bog_vmap_app_wire_fits(
		out: $bog_vmap_app_inspect_value_kind,
		into: $bog_vmap_app_inspect_value_kind,
	) {
		const loose = new Set< $bog_vmap_app_inspect_value_kind >([ 'null', 'get', 'bind' ])
		if( loose.has( out ) || loose.has( into ) ) return true

		const norm = ( kind: $bog_vmap_app_inspect_value_kind ) => kind === 'locale' ? 'string' : kind

		return norm( out ) === norm( into )
	}

	export function $bog_vmap_app_wire_takes(
		out: $bog_vmap_app_inspect_value_kind,
		into: $bog_vmap_app_wire_port,
		bidi: boolean,
	) {
		if( !$bog_vmap_app_wire_fits( out, into.kind ) ) return false

		return bidi ? into.next : true
	}

	export function $bog_vmap_app_wire_label( line: {
		readonly label: string
		readonly bidi: boolean
	} ) {
		return [ line.bidi ? $bog_vmap_app_wire_sign : '', line.label ].filter( Boolean ).join( ' ' )
	}

	export function $bog_vmap_app_wire_name( dot: {
		readonly port: { readonly name: string, readonly next: boolean, readonly label?: string }
		readonly hint: string
	} ) {
		const name = ( dot.port.label || dot.port.name ) + ( dot.port.next ? '?' : '' )

		return dot.hint ? `${ name } · ${ dot.hint }` : name
	}

	export function $bog_vmap_app_wire_port_point(
		box: $bog_vmap_app_wire_box,
		side: $bog_vmap_app_wire_side,
		index: number,
		lift = 0,
	): readonly [ number, number ] {
		const x = side === 'in'
			? box.left - $bog_vmap_app_wire_gap
			: box.left + box.width + $bog_vmap_app_wire_gap

		const y = box.top - lift + $bog_vmap_app_wire_row / 2 + index * $bog_vmap_app_wire_row

		return [ x, y ]
	}

	export function $bog_vmap_app_wire_off(
		box: $bog_vmap_app_wire_box,
		point: readonly [ number, number ],
		count = 1,
		lift = 0,
	) {
		const dx = Math.min(
			Math.abs( point[0] - ( box.left - $bog_vmap_app_wire_gap ) ),
			Math.abs( point[0] - ( box.left + box.width + $bog_vmap_app_wire_gap ) ),
		)

		const first = box.top - lift + $bog_vmap_app_wire_row / 2
		const index = Math.round( ( point[1] - first ) / $bog_vmap_app_wire_row )
		const held = Math.max( 0, Math.min( count - 1, index ) )

		return Math.hypot( dx, point[1] - ( first + held * $bog_vmap_app_wire_row ) )
	}

	export function $bog_vmap_app_wire_lift(
		box: $bog_vmap_app_wire_box,
		count: number,
		height: number,
	) {
		const over = box.top + count * $bog_vmap_app_wire_row - height
		if( over <= 0 ) return 0

		return Math.min( over, Math.max( box.top, 0 ) )
	}

	export function $bog_vmap_app_wire_side_point(
		box: $bog_vmap_app_wire_box,
		side: $bog_vmap_app_wire_side,
	): readonly [ number, number ] {
		return $bog_vmap_app_wire_port_point( box, side, 0 )
	}

	export function $bog_vmap_app_wire_over(
		box: $bog_vmap_app_wire_box,
		point: readonly [ number, number ],
		count = 1,
		lift = 0,
	) {
		const reach = $bog_vmap_app_wire_gap + $bog_vmap_app_wire_hit

		const top = Math.min( box.top, box.top - lift )
		const bottom = Math.max( box.top + box.height, top + count * $bog_vmap_app_wire_row )

		if( point[1] < top ) return false
		if( point[1] > bottom ) return false

		if( point[1] >= box.top && point[1] <= box.top + box.height ) {
			if( point[0] < box.left - reach ) return false
			if( point[0] > box.left + box.width + reach ) return false
			return true
		}

		const strip = $bog_vmap_app_wire_hit + $bog_vmap_app_wire_radius

		if( Math.abs( point[0] - ( box.left - $bog_vmap_app_wire_gap ) ) <= strip ) return true
		if( Math.abs( point[0] - ( box.left + box.width + $bog_vmap_app_wire_gap ) ) <= strip ) return true

		return false
	}

	function wire_reach( span: number ) {
		return Math.max( 40, Math.abs( span ) / 2 )
	}

	function wire_control(
		from: readonly [ number, number ],
		to: readonly [ number, number ],
	): readonly [ readonly [ number, number ], readonly [ number, number ] ] {
		if( to[0] >= from[0] ) {
			const reach = wire_reach( to[0] - from[0] )
			return [ [ from[0] + reach, from[1] ], [ to[0] - reach, to[1] ] ]
		}

		const reach = wire_reach( to[1] - from[1] )
		const down = to[1] >= from[1] ? 1 : -1

		return [ [ from[0], from[1] + reach * down ], [ to[0], to[1] - reach * down ] ]
	}

	export function $bog_vmap_app_wire_curve(
		from: readonly [ number, number ],
		to: readonly [ number, number ],
	) {
		const [ one, two ] = wire_control( from, to )

		return `M ${ from[0] } ${ from[1] } C ${ one[0] } ${ one[1] }, ${ two[0] } ${ two[1] }, ${ to[0] } ${ to[1] }`
	}

	export function $bog_vmap_app_wire_curve_mid(
		from: readonly [ number, number ],
		to: readonly [ number, number ],
	): readonly [ number, number ] {
		const [ one, two ] = wire_control( from, to )

		return [
			( from[0] + 3 * one[0] + 3 * two[0] + to[0] ) / 8,
			( from[1] + 3 * one[1] + 3 * two[1] + to[1] ) / 8,
		]
	}

	export function $bog_vmap_app_wire_dot_at(
		dots: readonly $bog_vmap_app_wire_dot[],
		point: readonly [ number, number ],
	) {
		let found = null as $bog_vmap_app_wire_dot | null
		let best = Infinity

		for( const dot of dots ) {
			const span = Math.hypot( dot.x - point[0], dot.y - point[1] )
			if( span > $bog_vmap_app_wire_hit ) continue
			if( found && span > best ) continue

			found = dot
			best = span
		}

		return found
	}

}
